/**
 * Fallback list of jurisdictions that have case / precedent indexes.
 *
 * The authoritative source is now storage-account discovery on the backend
 * (GET /matters/dropdowns → each jurisdiction row carries `has_cases`). This
 * static array is only a fallback used before the first fetch completes or if
 * discovery is unavailable. Keep it in sync with the known deployed indexes.
 */
export const CASES_AVAILABLE_JURISDICTIONS = ['SE', 'NL'];

/**
 * Dynamic case-law support, populated from the backend jurisdiction rows.
 * `null` until the first /matters/dropdowns fetch resolves, at which point we
 * fall back to the static array above.
 */
let _dynamicCasesSupport: Set<string> | null = null;

/**
 * Update the dynamic case-law support set from fetched jurisdiction rows.
 * Called by the fetchMatterDropdowns action. Each row is expected to expose a
 * `code` and a `has_cases` boolean (the backend merged_jurisdictions shape).
 */
export const setJurisdictionCasesSupport = (
  rows: Array<{ code?: string; has_cases?: boolean }> | null | undefined,
): void => {
  if (!Array.isArray(rows) || rows.length === 0) return;
  const next = new Set<string>();
  rows.forEach((row) => {
    if (row && row.has_cases && row.code) {
      next.add(String(row.code).toUpperCase());
    }
  });
  _dynamicCasesSupport = next;
};

/**
 * Check whether a given jurisdiction code supports case-law / precedent features.
 * Uses the dynamic (discovery-backed) set when available, otherwise the static
 * fallback array.
 */
export const hasCasesSupport = (jurisdiction?: string | null): boolean => {
  if (!jurisdiction) return false;
  const code = jurisdiction.toUpperCase();
  if (_dynamicCasesSupport !== null) {
    return _dynamicCasesSupport.has(code);
  }
  return CASES_AVAILABLE_JURISDICTIONS.includes(code);
};

/**
 * Fallback list of jurisdictions available in the platform.
 * Used by onboarding, settings, and matter creation forms only when the
 * backend (storage-account discovery) list is unavailable.
 */
export const AVAILABLE_JURISDICTIONS = [
  { value: 'SE', label: 'Sweden' },
  { value: 'NO', label: 'Norway' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'ES', label: 'Spain' },
];
